import {
  useAppKit,
  useAppKitAccount,
  useAppKitNetwork,
  useAppKitProvider,
  useDisconnect,
} from "@reown/appkit/react";
import type { ReactNode } from "react";
import { WagmiProvider } from "wagmi";
import { NETWORK_CONFIG } from "../utils/config";
import {
  type TransactionRequest,
  type TransactionResponse,
  type WalletAccount,
  WalletAdapterStatus,
} from "../utils/types";
import { initializeAppKit, initializeWagmiAdapter } from "../utils/wallet-initialization";
import { WalletContext, type WalletContextType } from "./shared-wallet-context";

// EIP-1193 Provider interface
interface EIP1193Provider {
  request: (args: { method: string; params: unknown[] }) => Promise<unknown>;
}

interface InternalWalletProviderProps {
  children: ReactNode;
}

export function InternalWalletProvider({ children }: InternalWalletProviderProps) {
  // Initialize Wagmi adapter and AppKit only when this component renders
  const adapter = initializeWagmiAdapter();
  initializeAppKit(adapter);

  // Wrap children with WagmiProvider to provide Wagmi context for AppKit hooks
  return (
    <WagmiProvider config={adapter.wagmiConfig}>
      <InternalWalletProviderWithHooks>{children}</InternalWalletProviderWithHooks>
    </WagmiProvider>
  );
}

// Component that uses AppKit hooks (must be inside AppKit context)
function InternalWalletProviderWithHooks({ children }: InternalWalletProviderProps) {
  // AppKit hooks
  const appKit = useAppKit();
  const appKitAccount = useAppKitAccount();
  const appKitNetwork = useAppKitNetwork();
  const { disconnect } = useDisconnect();
  const { walletProvider } = useAppKitProvider("eip155");

  // Derive state from AppKit
  const selectedAccount: WalletAccount | null = appKitAccount.address
    ? {
        address: appKitAccount.address,
        chainId: Number(appKitNetwork.chainId || NETWORK_CONFIG.defaultChainId),
        name: "Wallet",
      }
    : null;

  const status: WalletAdapterStatus = appKitAccount.isConnected
    ? WalletAdapterStatus.CONNECTED
    : appKitAccount.status === "connecting"
      ? WalletAdapterStatus.CONNECTING
      : WalletAdapterStatus.DISCONNECTED;

  // Connect function
  const connect = async (): Promise<void> => {
    await appKit.open();
  };

  // Send transaction
  const sendTransaction = async (tx: TransactionRequest): Promise<TransactionResponse> => {
    if (!selectedAccount) {
      throw new Error("No account selected");
    }

    const provider = walletProvider as EIP1193Provider;
    const formattedValue = tx.value ? `0x${BigInt(tx.value).toString(16)}` : undefined;

    const hash = await provider.request({
      method: "eth_sendTransaction",
      params: [
        {
          from: selectedAccount.address,
          to: tx.to,
          value: formattedValue,
          data: tx.data,
          chainId: tx.chainId,
        },
      ],
    });

    return {
      hash: hash as string,
      chainId: tx.chainId || selectedAccount.chainId,
    };
  };

  // Switch chain
  const switchChain = async (): Promise<boolean> => {
    await appKit.open({ view: "Networks" });
    return true;
  };

  const contextValue: WalletContextType = {
    status,
    selectedAccount,
    connect,
    disconnect,
    sendTransaction,
    switchChain,
  };

  return <WalletContext.Provider value={contextValue}>{children}</WalletContext.Provider>;
}
